/**
 * 波形绘制服务
 * 使用 Web Audio API 分析音频，Canvas 绘制简单振幅条
 *
 * 设计决策：
 * - 使用原生 Canvas 而非 wavesurfer.js，减少包大小
 * - 简单振幅条设计，足够进行视觉对比
 */

export interface WaveformData {
  /** 归一化的振幅数据 (0-1) */
  amplitudes: number[];
  /** 音频时长 (ms) */
  durationMs: number;
  /** 采样率 */
  sampleRate: number;
}

export interface DrawWaveformOptions {
  /** Canvas 元素 */
  canvas: HTMLCanvasElement;
  /** 波形数据 */
  data: WaveformData;
  /** 柱子颜色 */
  color?: string;
  /** 背景颜色 */
  backgroundColor?: string;
  /** 柱子宽度 */
  barWidth?: number;
  /** 柱子间隔 */
  barGap?: number;
  /** 播放进度 (0-1) */
  progress?: number;
  /** 进度颜色 */
  progressColor?: string;
}

/**
 * 从音频 Blob 提取波形数据
 */
export async function extractWaveformData(
  blob: Blob,
  barCount: number = 50
): Promise<WaveformData> {
  const audioContext = new AudioContext();

  try {
    const arrayBuffer = await blob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0));

    const channelData = audioBuffer.getChannelData(0);
    const samplesPerBar = Math.floor(channelData.length / barCount);
    const amplitudes: number[] = [];

    for (let i = 0; i < barCount; i++) {
      const start = i * samplesPerBar;
      const end = Math.min(start + samplesPerBar, channelData.length);

      // 计算这个区间的 RMS (Root Mean Square)
      let sum = 0;
      for (let j = start; j < end; j++) {
        sum += channelData[j] * channelData[j];
      }
      const rms = Math.sqrt(sum / (end - start));
      amplitudes.push(rms);
    }

    // 归一化到 0-1
    const maxAmplitude = Math.max(...amplitudes, 0.001);
    const normalized = amplitudes.map((a) => Math.min(1, a / maxAmplitude));

    return {
      amplitudes: normalized,
      durationMs: audioBuffer.duration * 1000,
      sampleRate: audioBuffer.sampleRate,
    };
  } finally {
    await audioContext.close();
  }
}

/**
 * 在 Canvas 上绘制波形
 */
export function drawWaveform(options: DrawWaveformOptions): void {
  const {
    canvas,
    data,
    color = '#000000',
    backgroundColor = 'transparent',
    barWidth = 4,
    barGap = 2,
    progress,
    progressColor = '#7ec8e3',
  } = options;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const { width, height } = canvas;
  const { amplitudes } = data;

  // 清除画布
  ctx.clearRect(0, 0, width, height);

  // 绘制背景
  if (backgroundColor !== 'transparent') {
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);
  }

  // 计算实际可绘制的柱子数量
  const totalBarWidth = barWidth + barGap;
  const maxBars = Math.floor(width / totalBarWidth);
  const barsToShow = Math.min(amplitudes.length, maxBars);

  // 计算起始位置（居中）
  const totalWidth = barsToShow * totalBarWidth - barGap;
  const startX = (width - totalWidth) / 2;

  // 进度索引
  const progressIndex = progress !== undefined ? Math.floor(progress * barsToShow) : -1;

  // 绘制每个柱子
  for (let i = 0; i < barsToShow; i++) {
    const amplitude = amplitudes[Math.floor((i / barsToShow) * amplitudes.length)] || 0;
    const barHeight = Math.max(2, amplitude * (height - 4));

    const x = startX + i * totalBarWidth;
    const y = (height - barHeight) / 2;

    // 已播放部分使用进度颜色
    ctx.fillStyle = progress !== undefined && i <= progressIndex ? progressColor : color;
    ctx.fillRect(x, y, barWidth, barHeight);
  }
}

/**
 * 创建波形 Hook 的辅助函数
 */
export function createWaveformRenderer(canvas: HTMLCanvasElement) {
  let animationId: number | null = null;
  let currentData: WaveformData | null = null;
  let currentProgress = 0;

  const render = (options?: Partial<DrawWaveformOptions>) => {
    if (!currentData) return;

    drawWaveform({
      canvas,
      data: currentData,
      progress: currentProgress,
      ...options,
    });
  };

  return {
    setData(data: WaveformData) {
      currentData = data;
      render();
    },

    setProgress(progress: number) {
      currentProgress = progress;
      render();
    },

    update(options?: Partial<DrawWaveformOptions>) {
      render(options);
    },

    clear() {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    },

    getData() {
      return currentData;
    },
  };
}

/**
 * 从 DataURL 提取波形数据
 */
export async function extractWaveformFromDataUrl(
  dataUrl: string,
  barCount: number = 50
): Promise<WaveformData> {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return extractWaveformData(blob, barCount);
}
